import { queryOptions, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useRequiredParams } from "~utils/url";
import { ApiResponse } from "~api/api-response.model";
import { t } from "@lingui/core/macro";
import { assetsApiRequest } from "../api/api-request";
import { AlarmResponse, AlarmList } from "./alarm.model";
import { AlarmTab } from "./alarm.utils";

export type AlarmCount = {
  count: {
    all: number;
    active: number;
    unacknowledged: number;
  };
};

type AlarmListParams = {
  companyId: number;
  tab?: AlarmTab;
  search?: string;
  sort?: string;
  order?: string;
  page?: number;
  pageSize?: number;
  alarmGroups?: string[];
  lockStates?: string[];
  deviceId?: number;
  levelId?: number;
  maxResults?: number;
};

type AlarmCountParams = Omit<
  AlarmListParams,
  "page" | "pageSize" | "sort" | "order"
>;

type ExtraAlarmQueryOptions<T> = Omit<
  UseQueryOptions<T>,
  "queryKey" | "queryFn"
>;

export const alarmQueries = {
  list: (
    params: AlarmListParams,
    options?: ExtraAlarmQueryOptions<AlarmList[]>,
  ) =>
    queryOptions({
      queryKey: ["alarms", params],
      queryFn: () => fetchAlarms(params),
      ...options,
    }),
  count: (
    params: AlarmCountParams,
    options?: ExtraAlarmQueryOptions<AlarmCount>,
  ) =>
    queryOptions({
      queryKey: ["alarms-count", params],
      queryFn: () => fetchCount(params),
      ...options,
    }),
};

// Queries

export function useAlarmsCountQuery(
  params?: Omit<AlarmCountParams, "companyId">,
  options?: ExtraAlarmQueryOptions<AlarmCount>,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(alarmQueries.count({ companyId, ...params }, options));
}

export function useAlarmsQuery(
  params: Omit<AlarmListParams, "companyId">,
  options?: ExtraAlarmQueryOptions<AlarmList[]>,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(alarmQueries.list({ companyId, ...params }, options));
}

// Fetchers
async function fetchAlarms({
  companyId,
  alarmGroups,
  tab,
  ...params
}: AlarmListParams) {
  // Replace NO GROUP with null for the query since its actual value in the DB is null. NO GROUP is just shown to the user
  const actualGroups = alarmGroups?.map((g) => (g === t`NO GROUP` ? "" : g));
  return await assetsApiRequest
    .get<
      ApiResponse<AlarmResponse[]>
    >(`analytics/companies/${companyId}/alarms`, { params: { ...params, alarmGroups: actualGroups, nested: params.levelId ? true : undefined, alarmType: tab } })
    .then((resp) => resp.data.data.map((a) => new AlarmList(a)));
}

async function fetchCount({ companyId, ...params }: AlarmCountParams) {
  return assetsApiRequest
    .get<
      ApiResponse<AlarmCount>
    >(`analytics/companies/${companyId}/alarms/count`, { params })
    .then((resp) => resp.data.data);
}
